import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileSpreadsheet, Download, Clock, Users, Building2, Receipt } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' },
];

export default function ReportsPage() {
  const { isRole } = useAuth();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Work Hours Report filters
  const [workHoursYear, setWorkHoursYear] = useState(currentYear);
  const [workHoursMonth, setWorkHoursMonth] = useState(currentMonth);
  const [workHoursEmployee, setWorkHoursEmployee] = useState('__all__');
  const [workHoursSite, setWorkHoursSite] = useState('__all__');
  
  // Attendance Report filters
  const [attYear, setAttYear] = useState(currentYear);
  const [attMonth, setAttMonth] = useState(currentMonth);
  const [attEmployee, setAttEmployee] = useState('__all__');
  
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [empRes, sitesRes] = await Promise.all([
          api.get('/employees'),
          api.get('/sites'),
        ]);
        setEmployees(empRes.data.filter(e => e.is_active));
        setSites(sitesRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const downloadReport = async (type, params, filename) => {
    setDownloading(type);
    try {
      const response = await api.get(`/export/${type}`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Errore durante il download del report');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadWorkHours = () => {
    const params = {
      year: workHoursYear,
      month: workHoursMonth,
    };
    if (workHoursEmployee !== '__all__') params.employee_id = workHoursEmployee;
    if (workHoursSite !== '__all__') params.site_id = workHoursSite;
    
    downloadReport(
      'work-hours-report',
      params,
      `report_ore_lavorate_${workHoursYear}_${String(workHoursMonth).padStart(2, '0')}.csv`
    );
  };

  const handleDownloadAttendance = () => {
    const params = {
      year: attYear,
      month: attMonth,
    };
    if (attEmployee !== '__all__') params.employee_id = attEmployee;
    
    downloadReport(
      'attendance',
      params,
      `presenze_${attYear}_${String(attMonth).padStart(2, '0')}.csv`
    );
  };

  const handleDownloadEmployees = () => {
    downloadReport('employees', {}, `dipendenti_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleDownloadEquipment = () => {
    downloadReport('equipment', {}, `attrezzature_${new Date().toISOString().split('T')[0]}.csv`);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Report e Esportazioni</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Report Ore Lavorate */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="text-blue-600" size={20} />
              Report Ore Lavorate
            </CardTitle>
            <CardDescription>
              Dettaglio ore lavorate per dipendente con cantiere e rimborsi spese. Ideale per elaborazione buste paga.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Anno</Label>
                <Select value={String(workHoursYear)} onValueChange={(v) => setWorkHoursYear(parseInt(v))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mese</Label>
                <Select value={String(workHoursMonth)} onValueChange={(v) => setWorkHoursMonth(parseInt(v))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dipendente</Label>
                <Select value={workHoursEmployee} onValueChange={setWorkHoursEmployee}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutti i dipendenti</SelectItem>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Cantiere</Label>
                <Select value={workHoursSite} onValueChange={setWorkHoursSite}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutti i cantieri</SelectItem>
                    {sites.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={handleDownloadWorkHours} 
              disabled={downloading === 'work-hours-report'}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="download-work-hours-btn"
            >
              <Download size={16} className="mr-2" />
              {downloading === 'work-hours-report' ? 'Download in corso...' : 'Scarica Report Ore'}
            </Button>
          </CardContent>
        </Card>

        {/* Report Presenze */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSpreadsheet className="text-green-600" size={20} />
              Export Presenze
            </CardTitle>
            <CardDescription>
              Esporta le timbrature (entrata/uscita) per un determinato periodo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Anno</Label>
                <Select value={String(attYear)} onValueChange={(v) => setAttYear(parseInt(v))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mese</Label>
                <Select value={String(attMonth)} onValueChange={(v) => setAttMonth(parseInt(v))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs">Dipendente</Label>
                <Select value={attEmployee} onValueChange={setAttEmployee}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutti i dipendenti</SelectItem>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={handleDownloadAttendance} 
              disabled={downloading === 'attendance'}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="download-attendance-btn"
            >
              <Download size={16} className="mr-2" />
              {downloading === 'attendance' ? 'Download in corso...' : 'Scarica Presenze'}
            </Button>
          </CardContent>
        </Card>

        {/* Export Liste */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="text-purple-600" size={20} />
              Lista Dipendenti
            </CardTitle>
            <CardDescription>
              Esporta l'anagrafica completa di tutti i dipendenti attivi.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleDownloadEmployees} 
              disabled={downloading === 'employees'}
              variant="outline"
              className="w-full"
              data-testid="download-employees-btn"
            >
              <Download size={16} className="mr-2" />
              {downloading === 'employees' ? 'Download in corso...' : 'Scarica Lista Dipendenti'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="text-orange-600" size={20} />
              Lista Attrezzature
            </CardTitle>
            <CardDescription>
              Esporta l'elenco completo di tutte le attrezzature aziendali.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleDownloadEquipment} 
              disabled={downloading === 'equipment'}
              variant="outline"
              className="w-full"
              data-testid="download-equipment-btn"
            >
              <Download size={16} className="mr-2" />
              {downloading === 'equipment' ? 'Download in corso...' : 'Scarica Lista Attrezzature'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Receipt className="text-amber-600 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-medium text-amber-800">Formato File</p>
              <p className="text-xs text-amber-700 mt-1">
                Tutti i report vengono esportati in formato CSV (separatore: punto e virgola) 
                compatibile con Excel e altri fogli di calcolo. I numeri decimali usano la virgola come separatore.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
